import { Scene } from '../../types/Scene';
import { PlayRanges } from '../../types/Segment/PlayRange';

/**
 * Maps scenes from original timeline to playable timeline based on play ranges.
 * This is needed for composite segments that have trimmed/deleted sections (playRanges)
 * where scenes need to be adjusted to the new playable timeline.
 *
 * @param scenes - Array of scenes with frame timings relative to original timeline
 * @param playRanges - Array of play ranges defining which parts of the original timeline are actually played
 * @returns Array of scenes with frame timings adjusted to the playable timeline
 */
export function mapScenesToPlayableTimeline(scenes: Scene[] = [], playRanges: PlayRanges): Scene[] {
  if (scenes.length === 0) {
    return [];
  }

  // Helper function to map original frame to playable frame
  const mapOriginalFrameToPlayableFrame = (originalFrame: number): number => {
    let currentPlayableFrame = 0;

    // Walk through play ranges to build mapping
    for (const playRange of playRanges) {
      const rangeDuration = playRange.toFrame - playRange.fromFrame;

      // If our target original frame falls within this play range
      if (originalFrame >= playRange.fromFrame && originalFrame <= playRange.toFrame) {
        const offsetInRange = originalFrame - playRange.fromFrame;
        return currentPlayableFrame + offsetInRange;
      }

      // If our target frame is before this range, it maps to current playable frame
      if (originalFrame < playRange.fromFrame) {
        return currentPlayableFrame;
      }

      // Move past this play range
      currentPlayableFrame += rangeDuration;
    }

    // If original frame is after all play ranges, map to end of playable timeline
    return currentPlayableFrame;
  };

  // Map each scene from original frames to playable frames
  return scenes.map(scene => ({
    ...scene,
    fromFrame: mapOriginalFrameToPlayableFrame(scene.fromFrame),
    toFrame: mapOriginalFrameToPlayableFrame(scene.toFrame)
  }));
}
